import React from 'react';
import { Skeleton, SkeletonButton, SkeletonRegion } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '../../i18n';

/**
 * Placeholders for the module's two non-table row shapes.
 *
 * The setup views (مراکز هزینه، دوره‌ای، تأمین‌کنندگان) and the insurance
 * contracts are not tables: each is a stack of bordered `Row` boxes — 9px 12px
 * padding, radius 10, 8px apart — holding a name, a small meta and a few ghost
 * `sm` buttons. `SkeletonRow` is a `<tr>` and only belongs in a live `<tbody>`,
 * so these views used to put a table row into a `<div>`, which is invalid DOM
 * and the wrong height. This is the row THEY draw, at the size they draw it
 * (DESIGN.md §"Loading & skeleton" — geometry).
 *
 * A leaf on purpose: it imports nothing from the screen, so the page skeleton
 * (the router's eager fallback) can share it without dragging the screen into
 * the main bundle.
 */
export function SetupRowSkeleton({ actions = 2, meta = true, style }) {
  return (
    <div
      aria-hidden
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
        border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 8,
        background: '#fff', ...style,
      }}
    >
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <Skeleton width="34%" height={14} />
        {meta ? <Skeleton width="18%" height={11} /> : null}
      </div>
      {Array.from({ length: actions }, (_, i) => (
        <SkeletonButton key={i} size="sm" width={[52, 60, 48][i % 3]} />
      ))}
    </div>
  );
}

/** `count` setup rows, announced once. */
export function SetupRowsSkeleton({ count = 3, actions = 2, meta = true, label, style, ...rest }) {
  const { t } = useTranslation();
  return (
    <SkeletonRegion
      label={label ?? t('common.state.loading')}
      style={style}
      {...rest}>
      {Array.from({ length: count }, (_, i) => (
        <SetupRowSkeleton key={i} actions={actions} meta={meta} />
      ))}
    </SkeletonRegion>
  );
}

/**
 * The standard-list picker's rows: a group caption, then options of 7px 12px
 * padding holding a checkbox square, the name and a trailing chip. Same box as
 * `CatalogGroup`, so the list does not jump when the catalogue lands.
 */
export function CatalogRowsSkeleton({ groups = 2, rows = 4, multi = false, label, ...rest }) {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={label ?? t('common.state.loading')} {...rest}>
      {Array.from({ length: groups }, (_, g) => (
        <React.Fragment key={g}>
          <div style={{ padding: '9px 12px 4px' }}>
            <Skeleton width={72 + (g % 2) * 24} height={11} />
          </div>
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', minHeight: 34 }}>
              {multi ? <Skeleton width={14} height={14} radius={3} /> : null}
              <Skeleton width={`${40 + ((g * 3 + i) * 13) % 35}%`} height={13} />
              <span style={{ flex: 1 }} />
              {i % 3 === 0 ? <Skeleton width={44} height={16} radius={5} /> : null}
              <Skeleton width={22} height={11} />
            </div>
          ))}
        </React.Fragment>
      ))}
    </SkeletonRegion>
  );
}

/**
 * The cardex (stock movements) rows: a 28px disc, a label line with its
 * quantity on the end, and a date line — 10px 0 padding with a divider.
 */
export function MovementRowsSkeleton({ count = 3, label, ...rest }) {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={label ?? t('common.state.loading')} {...rest}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
          <Skeleton width={28} circle />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <Skeleton width={`${44 + (i * 17) % 30}%`} height={12} />
              <Skeleton width={36} height={12} />
            </div>
            <Skeleton width="30%" height={10} radius={RADIUS.chip} />
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}
